const API_ROOT = import.meta.env.VITE_APP_API_ROOT;

/**
 * API Service for Card Designer
 * Handles all backend communication
 */

// Design Management
export const getCardDesign = async (jobId, token = null) => {
  try {
    const response = await fetch(`${API_ROOT}/api/customizer/jobs/${jobId}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
    });
    const data = await response.json();

    return {
      front: data.design?.front,
      back: data.design?.back,
      type: data.type,
      variant: data.variant,
      notificationSent: data.notificationSent,
    };
  } catch (error) {
    console.error("Error fetching card design:", error);
    throw error;
  }
};

export const sendDesignEmail = async (jobId, payload) => {
  try {
    const response = await fetch(`${API_ROOT}/api/customizer/jobs/${jobId}/email`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      console.error("Error response from sendDesignEmail:", response.status);
      return false;
    }

    const data = await response.json();
    return !!data.success;
  } catch (error) {
    console.error("Error sending design email:", error);
    return false;
  }
};

export const saveCardDesign = async (designData, token = null) => {
  try {
    const response = await fetch(`${API_ROOT}/api/customizer/jobs`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: JSON.stringify(designData),
    });
    const data = await response.json();

    if (data?.error?.errorKey || data.success === false) {
      throw new Error(data?.error?.errorKey || "Save failed");
    }

    return data;
  } catch (error) {
    console.error("Error saving card design:", error);
    throw error;
  }
};

export const batchSaveDesigns = async (designIds, originalDesignId, token = null) => {
  try {
    // Note: Batch save is deprecated - use individual saveCardDesign calls instead
    console.warn('batchSaveDesigns is deprecated');
    const savedDesigns = [];

    for (const designId of designIds) {
      const result = await saveCardDesign({
        design_id: designId,
        original_design_id: originalDesignId
      }, token);
      savedDesigns.push(result.job_id);
    }

    return { design_ids: savedDesigns };
  } catch (error) {
    console.error("Error batch saving designs:", error);
    throw error;
  }
};

// Template Management
export const getTemplates = async (type = "print") => {
  try {
    const response = await fetch(`${API_ROOT}/api/customizer/templates`);
    const data = await response.json();
    return data.templates || [];
  } catch (error) {
    console.error("Error fetching templates:", error);
    throw error;
  }
};

// Sticker Management
export const getStickers = async () => {
  try {
    const response = await fetch(`${API_ROOT}/api/customizer/stickers`);
    const data = await response.json();
    return data.stickers || [];
  } catch (error) {
    console.error("Error fetching stickers:", error);
    throw error;
  }
};

export const getTemplate = async (templateId, type = "print") => {
  try {
    const response = await fetch(`${API_ROOT}/api/customizer/templates/${templateId}`);
    const data = await response.json();
    return data.template || null;
  } catch (error) {
    console.error("Error fetching template:", error);
    throw error;
  }
};

// Order Management (for Products.vue)
export const getOrderByHash = async (orderHash, token = null) => {
  try {
    const response = await fetch(`${API_ROOT}/api/customizer/orders/${orderHash}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
    });
    const { data } = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching order:", error);
    throw error;
  }
};

// Legacy function - deprecated, use getOrderByHash instead
export const getOrderJobs = async (orderId, token = null) => {
  console.warn('getOrderJobs is deprecated, use getOrderByHash with order hash instead');
  return getOrderByHash(orderId, token);
};

// Note: Order submission is now handled via Medusa webhooks
export const submitOrder = async (orderId, token = null) => {
  console.warn('submitOrder is deprecated - orders are created via Medusa webhooks');
  throw new Error('Order submission is handled by Medusa webhooks');
};

// Email Management
export const sendQRCodeEmail = async (emailData, token = null) => {
  try {
    const response = await fetch(`${API_ROOT}/api/customizer/emails/send-qr`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: JSON.stringify(emailData),
    });
    const data = await response.json();

    if (!response.ok || data.success === false) {
      throw new Error(data?.error?.message || "Failed to send email");
    }

    return data;
  } catch (error) {
    console.error("Error sending QR code email:", error);
    throw error;
  }
};
